import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import { CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config';
import './StaffHarvestingPage.css';

const StaffHarvestingPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [contactContent, setContactContent] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [modalShake, setModalShake] = useState(false);
  const authToken = localStorage.getItem('authToken');
  const cipReady = !!CIP_APP_URL;
  const canBridgeToCip = cipReady && !!authToken;
  const cipBase = CIP_APP_URL ? CIP_APP_URL.replace(/\/$/, '') : '';
  const returnUrl = `${window.location.origin}/staff/harvesting`;
  const cipReportPath = `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting?embedded=1&rsminReturn=${encodeURIComponent(returnUrl)}&rsminLabel=${encodeURIComponent('Back to RSMIN Staff Harvesting')}`;
  const cipReportBridgeUrl = canBridgeToCip
    ? `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(cipReportPath)}`
    : '';

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };
  
  // Education resources state
  const [educationResources, setEducationResources] = useState([]);
  const [educationLoading, setEducationLoading] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [educationFormData, setEducationFormData] = useState({
    title: '',
    category: 'Community Stories',
    description: '',
    content: '',
    thumbnailUrl: '',
    videoUrl: '',
    externalLink: '',
    tags: [],
    isPublished: false,
    isFeatured: false,
    order: 0
  });

  // Information & Permits state
  const [informationResources, setInformationResources] = useState([]);
  const [informationLoading, setInformationLoading] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [editingInformation, setEditingInformation] = useState(null);
  const [informationFormData, setInformationFormData] = useState({
    title: '',
    category: 'Permits & Licenses',
    description: '',
    content: '',
    thumbnailUrl: '',
    videoUrl: '',
    externalLink: '',
    tags: [],
    isPublished: false,
    isFeatured: false,
    order: 0,
    effectiveDate: '',
    expiryDate: '',
    attachments: []
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  // ── Harvest Categories state ───────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', icon: '📦', description: '', isActive: true, subcategories: [] });
  const [newSubcatName, setNewSubcatName] = useState('');

  // Check if returning from location picker on mount
  const [activeSection, setActiveSection] = useState('posts');

  const previewPath = useMemo(() => {
    switch (activeSection) {
      case 'posts':
        return '/harvesting';
      case 'education':
        return '/harvesting/education';
      case 'permits':
        return '/harvesting/information';
      case 'contact':
        return '/harvesting/contact';
      default:
        return '/harvesting';
    }
  }, [activeSection]);

  const openPreviewWindow = (viewAs) => {
    const query = new URLSearchParams({ viewAs }).toString();
    const url = `${window.location.origin}${previewPath}?${query}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const stripHtml = (value) => {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    fetchPosts();
    if (activeSection === 'contact') {
      fetchContactInfo();
    }
    if (activeSection === 'education') {
      fetchEducationResources();
    }
    if (activeSection === 'permits') {
      fetchInformationResources();
    }
    if (activeSection === 'categories') {
      fetchCategories();
    }
  }, [activeSection]);



  const fetchPosts = async () => {
    try {
      console.log('Fetching all posts (staff view)...');
      const response = await api.get('/api/harvesting-posts');
      console.log('Staff posts response:', response.data);
      setPosts(response.data);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err.response?.data?.message || 'Error fetching posts');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await api.delete(`/api/harvesting-posts/${id}`);
        fetchPosts();
      } catch (err) {
        setError('Error deleting post');
      }
    }
  };

  const fetchContactInfo = async () => {
    try {
      setContactLoading(true);
      const response = await api.get('/api/harvesting-contact');
      setContactContent(response.data.content || '');
    } catch (err) {
      console.error('Error fetching contact info:', err);
      setError('Error loading contact information');
    } finally {
      setContactLoading(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      setContactLoading(true);
      setError('');
      setSuccessMessage('');
      await api.put('/api/harvesting-contact', { content: contactContent });
      setSuccessMessage('Contact information saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving contact info:', err);
      setError(err.response?.data?.message || 'Error saving contact information');
    } finally {
      setContactLoading(false);
    }
  };

  // Education resources functions
  const fetchEducationResources = async () => {
    try {
      setEducationLoading(true);
      setError('');
      const response = await api.get('/api/harvesting/education');
      setEducationResources(response.data.resources || []);
    } catch (err) {
      console.error('Error fetching education resources:', err);
      setError('Error loading education resources');
    } finally {
      setEducationLoading(false);
    }
  };

  const handleSaveEducation = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      if (editingEducation) {
        await api.put(`/api/harvesting/education/${editingEducation._id}`, educationFormData);
        setSuccessMessage('Education resource updated successfully!');
      } else {
        await api.post('/api/harvesting/education', educationFormData);
        setSuccessMessage('Education resource created successfully!');
      }

      setShowEducationModal(false);
      resetEducationForm();
      fetchEducationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving education resource:', err);
      setError(err.response?.data?.error || 'Error saving education resource');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEducation = (resource) => {
    setEditingEducation(resource);
    setEducationFormData({
      title: resource.title,
      category: resource.category,
      description: resource.description,
      content: resource.content,
      thumbnailUrl: resource.thumbnailUrl || '',
      videoUrl: resource.videoUrl || '',
      externalLink: resource.externalLink || '',
      tags: resource.tags || [],
      isPublished: resource.isPublished,
      isFeatured: resource.isFeatured,
      order: resource.order || 0
    });
    setShowEducationModal(true);
  };

  const handleDeleteEducation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this education resource?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/harvesting/education/${id}`);
      setSuccessMessage('Education resource deleted successfully!');
      fetchEducationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting education resource:', err);
      setError(err.response?.data?.error || 'Error deleting education resource');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await api.patch(`/api/harvesting/education/${id}/toggle-publish`);
      fetchEducationResources();
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Error updating publish status');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await api.patch(`/api/harvesting/education/${id}/toggle-featured`);
      fetchEducationResources();
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Error updating featured status');
    }
  };

  const resetEducationForm = () => {
    setEditingEducation(null);
    setEducationFormData({
      title: '',
      category: 'Community Stories',
      description: '',
      content: '',
      thumbnailUrl: '',
      videoUrl: '',
      externalLink: '',
      tags: [],
      isPublished: false,
      isFeatured: false,
      order: 0
    });
  };

  // Information & Permits functions
  const fetchInformationResources = async () => {
    try {
      setInformationLoading(true);
      setError('');
      const response = await api.get('/api/harvesting/information');
      setInformationResources(response.data.resources || []);
    } catch (err) {
      console.error('Error fetching information resources:', err);
      setError('Error loading information resources');
    } finally {
      setInformationLoading(false);
    }
  };

  const handleSaveInformation = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      if (editingInformation) {
        await api.put(`/api/harvesting/information/${editingInformation._id}`, informationFormData);
        setSuccessMessage('Information updated successfully!');
      } else {
        await api.post('/api/harvesting/information', informationFormData);
        setSuccessMessage('Information created successfully!');
      }

      setShowInformationModal(false);
      resetInformationForm();
      fetchInformationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving information:', err);
      setError(err.response?.data?.error || 'Error saving information');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInformation = (resource) => {
    setEditingInformation(resource);
    setInformationFormData({
      title: resource.title,
      category: resource.category,
      description: resource.description,
      content: resource.content,
      thumbnailUrl: resource.thumbnailUrl || '',
      videoUrl: resource.videoUrl || '',
      externalLink: resource.externalLink || '',
      tags: resource.tags || [],
      isPublished: resource.isPublished,
      isFeatured: resource.isFeatured,
      order: resource.order || 0,
      effectiveDate: resource.effectiveDate ? resource.effectiveDate.split('T')[0] : '',
      expiryDate: resource.expiryDate ? resource.expiryDate.split('T')[0] : '',
      attachments: resource.attachments || []
    });
    setShowInformationModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type (allow PDFs and common document types)
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF and Word documents are allowed');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFile(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const newAttachment = {
          filename: response.data.filename,
          url: response.data.url,
          fileType: response.data.fileType,
          size: response.data.size
        };

        setInformationFormData({
          ...informationFormData,
          attachments: [...informationFormData.attachments, newAttachment]
        });

        setSuccessMessage('File uploaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.error || 'Error uploading file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error uploading file');
    } finally {
      setUploadingFile(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = informationFormData.attachments.filter((_, i) => i !== index);
    setInformationFormData({
      ...informationFormData,
      attachments: newAttachments
    });
  };

  const handleDeleteInformation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this information?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/harvesting/information/${id}`);
      setSuccessMessage('Information deleted successfully!');
      fetchInformationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting information:', err);
      setError(err.response?.data?.error || 'Error deleting information');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublishInfo = async (id) => {
    try {
      await api.patch(`/api/harvesting/information/${id}/toggle-publish`);
      fetchInformationResources();
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Error updating publish status');
    }
  };

  const handleToggleFeaturedInfo = async (id) => {
    try {
      await api.patch(`/api/harvesting/information/${id}/toggle-featured`);
      fetchInformationResources();
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Error updating featured status');
    }
  };

  const resetInformationForm = () => {
    setEditingInformation(null);
    setInformationFormData({
      title: '',
      category: 'Permits & Licenses',
      description: '',
      content: '',
      thumbnailUrl: '',
      videoUrl: '',
      externalLink: '',
      tags: [],
      isPublished: false,
      isFeatured: false,
      order: 0,
      effectiveDate: '',
      expiryDate: '',
      attachments: []
    });
  };

  // ── Harvest Category API functions ─────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      const res = await api.get('/api/harvesting-categories/all');
      setCategories(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading categories');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) return;
    try {
      setLoading(true);
      setError('');
      if (editingCategory) {
        await api.put(`/api/harvesting-categories/${editingCategory._id}`, categoryFormData);
        setSuccessMessage('Category updated.');
      } else {
        await api.post('/api/harvesting-categories/create', categoryFormData);
        setSuccessMessage('Category created.');
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      fetchCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/api/harvesting-categories/${id}`);
      fetchCategories();
    } catch (err) {
      setError('Error deleting category');
    }
  };

  const handleToggleCategoryActive = async (cat) => {
    try {
      await api.put(`/api/harvesting-categories/${cat._id}`, { ...cat, isActive: !cat.isActive });
      fetchCategories();
    } catch (err) {
      setError('Error updating category');
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('Initialize default categories? This will only add categories if none exist yet.')) return;
    try {
      await api.post('/api/harvesting-categories/initialize');
      setSuccessMessage('Default categories initialized.');
      fetchCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error initializing defaults');
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', icon: '📦', description: '', isActive: true, subcategories: [] });
    setNewSubcatName('');
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryFormData({
      name: cat.name,
      icon: cat.icon || '📦',
      description: cat.description || '',
      isActive: cat.isActive,
      subcategories: cat.subcategories ? cat.subcategories.map((s) => ({ ...s })) : [],
    });
    setNewSubcatName('');
    setShowCategoryModal(true);
  };

  const addSubcat = () => {
    const name = newSubcatName.trim();
    if (!name) return;
    setCategoryFormData((prev) => ({ ...prev, subcategories: [...prev.subcategories, { name }] }));
    setNewSubcatName('');
  };

  const removeSubcat = (idx) => {
    setCategoryFormData((prev) => ({ ...prev, subcategories: prev.subcategories.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="staff-harvesting-container">
      <div className="harvesting-header">
        <h1>Harvesting Portal Management</h1>
        <p className="subtitle">Manage harvesting resources and content</p>
      </div>

      {/* Navigation Tabs */}
      <div className="harvesting-nav">
        <button 
          className={activeSection === 'posts' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('posts')}
        >
          📝 Posts
        </button>
        <button 
          className={activeSection === 'education' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('education')}
        >
          🎓 Harvesting Education
        </button>
        <button 
          className={activeSection === 'permits' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('permits')}
        >
          📋 Permits
        </button>
        <button
          className={activeSection === 'categories' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('categories')}
        >
          🌿 Categories
        </button>
        <button 
          className={activeSection === 'report' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('report')}
        >
          📊 Harvesting Report
        </button>
        <button 
          className={activeSection === 'contact' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('contact')}
        >
          📞 Contact
        </button>
      </div>

      <div className="harvesting-preview-toolbar">
        <div className="preview-toolbar-text">
          Preview this section in a new window before publishing.
        </div>
        <div className="preview-toolbar-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => openPreviewWindow('public')}
          >
            Open as Public
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => openPreviewWindow('citizen')}
          >
            Open as Citizen
          </button>
        </div>
        <div className="preview-toolbar-note">Unsaved edits are not shown in preview until you save.</div>
      </div>

      {/* Content Sections */}
      {activeSection === 'posts' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Manage Harvesting Blog Posts</h2>
            <button
              className="btn-primary"
              onClick={() => navigate('/staff/harvesting/posts/new')}
            >
              + Create Post
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}

          <div className="posts-list">
            <h3>Existing Posts ({posts.length})</h3>
            {posts.length === 0 ? (
              <p className="no-posts">No posts created yet.</p>
            ) : (
              <div className="posts-grid">
                {posts.map((post) => (
                  <div key={post._id} className="post-card">
                    {post.imageUrl && (
                      <div className="post-image">
                        <img src={post.imageUrl} alt={post.title} />
                      </div>
                    )}
                    <h4>{post.title}</h4>
                    <p className="post-content">
                      {(stripHtml(post.content) || '').substring(0, 150)}...
                    </p>
                    <div className="post-dates">
                      <p>
                        <strong>Publish:</strong>{' '}
                        {new Date(post.publishDate).toLocaleString('en-US', { timeZone: 'America/Toronto' })}
                      </p>
                      <p>
                        <strong>Unpublish:</strong>{' '}
                        {new Date(post.unpublishDate).toLocaleString('en-US', { timeZone: 'America/Toronto' })}
                      </p>
                    </div>
                    <div className="post-actions">
                      <button className="btn-edit" onClick={() => navigate(`/staff/harvesting/posts/${post._id}/edit`)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(post._id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {activeSection === 'education' && (
        <div className="section-content">
          <div className="section-header">
            <h2>🎓 Harvesting Education Board</h2>
            <button 
              className="btn-primary" 
              onClick={() => {
                resetEducationForm();
                setShowEducationModal(true);
              }}
            >
              + Create Post
            </button>
          </div>

          <p className="section-description">
            Create and share educational content, stories, study guides, and information with your community.
            Posts can include campfire stories, traditional knowledge, test information, safety guides, and more.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {educationLoading ? (
            <div className="loading-spinner">Loading education resources...</div>
          ) : (
            <div className="education-resources-grid">
              {educationResources.length === 0 ? (
                <div className="empty-state">
                  <p>No posts yet. Click "Create Post" to share your first story, guide, or announcement!</p>
                </div>
              ) : (
                educationResources.map(resource => (
                  <div key={resource._id} className="education-card">
                    <div className="card-header">
                      <h3>{resource.title}</h3>
                      <div className="card-badges">
                        {resource.isFeatured && <span className="badge featured">⭐ Featured</span>}
                        <span className={`badge ${resource.isPublished ? 'published' : 'draft'}`}>
                          {resource.isPublished ? '✓ Published' : '📝 Draft'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-meta">
                      <span className="category-badge">{resource.category}</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="tags">
                          {resource.tags.map((tag, idx) => (
                            <span key={idx} className="tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="description">{resource.description}</p>

                    {resource.thumbnailUrl && (
                      <div className="thumbnail-preview">
                        <img src={resource.thumbnailUrl} alt={resource.title} />
                      </div>
                    )}

                    <div className="resource-meta">
                      {resource.videoUrl && <span className="icon-badge">🎥 Video</span>}
                      {resource.attachments && resource.attachments.length > 0 && (
                        <span className="icon-badge">📎 {resource.attachments.length} file(s)</span>
                      )}
                      {resource.externalLink && <span className="icon-badge">🔗 Link</span>}
                      <span className="views">👁 {resource.viewCount || 0} views</span>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEditEducation(resource)}
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleTogglePublish(resource._id)}
                        title={resource.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        {resource.isPublished ? '📥 Unpublish' : '📤 Publish'}
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleToggleFeatured(resource._id)}
                        title={resource.isFeatured ? 'Unfeature' : 'Feature'}
                      >
                        {resource.isFeatured ? '⭐ Unfeature' : '☆ Feature'}
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDeleteEducation(resource._id)}
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'permits' && (
        <div className="section-content">
          <div className="section-header">
            <h2>📋 Information & Permits</h2>
            <button 
              className="btn-primary" 
              onClick={() => {
                resetInformationForm();
                setShowInformationModal(true);
              }}
            >
              + Create Information
            </button>
          </div>

          <p className="section-description">
            Manage permits, regulations, guidelines, and important harvesting information for your community.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {informationLoading ? (
            <div className="loading-spinner">Loading information...</div>
          ) : (
            <div className="education-resources-grid">
              {informationResources.length === 0 ? (
                <div className="empty-state">
                  <p>No information yet. Click "Create Information" to add permits, regulations, or guidelines!</p>
                </div>
              ) : (
                informationResources.map(resource => (
                  <div key={resource._id} className="education-card">
                    <div className="card-header">
                      <h3>{resource.title}</h3>
                      <div className="card-badges">
                        {resource.isFeatured && <span className="badge featured">⭐ Featured</span>}
                        <span className={`badge ${resource.isPublished ? 'published' : 'draft'}`}>
                          {resource.isPublished ? '✓ Published' : '📝 Draft'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-meta">
                      <span className="category-badge">{resource.category}</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="tags">
                          {resource.tags.map((tag, idx) => (
                            <span key={idx} className="tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="description">{resource.description}</p>

                    {resource.thumbnailUrl && (
                      <div className="thumbnail-preview">
                        <img src={resource.thumbnailUrl} alt={resource.title} />
                      </div>
                    )}

                    <div className="resource-meta">
                      {resource.videoUrl && <span className="icon-badge">🎥 Video</span>}
                      {resource.attachments && resource.attachments.length > 0 && (
                        <span className="icon-badge">📎 {resource.attachments.length} file(s)</span>
                      )}
                      {resource.externalLink && <span className="icon-badge">🔗 Link</span>}
                      <span className="views">👁 {resource.viewCount || 0} views</span>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEditInformation(resource)}
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleTogglePublishInfo(resource._id)}
                        title={resource.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        {resource.isPublished ? '📥 Unpublish' : '📤 Publish'}
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleToggleFeaturedInfo(resource._id)}
                        title={resource.isFeatured ? 'Unfeature' : 'Feature'}
                      >
                        {resource.isFeatured ? '⭐ Unfeature' : '☆ Feature'}
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDeleteInformation(resource._id)}
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'categories' && (
        <div className="section-content">
          <div className="section-header">
            <h2>🌿 Harvest Categories</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleInitializeDefaults}>Initialize Defaults</button>
              <button className="btn-primary" onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}>+ Add Category</button>
            </div>
          </div>
          <p className="section-description">Manage the categories and method/context options available when community members log harvesting activity.</p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {categoryLoading ? (
            <div className="loading-spinner">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="empty-state">
              <p>No categories yet. Click "Initialize Defaults" to add the standard set, or "+ Add Category" to create one.</p>
            </div>
          ) : (
            <div className="category-list">
              {categories.map((cat) => (
                <div key={cat._id} className={`category-row${cat.isActive ? '' : ' category-inactive'}`}>
                  <span className="category-icon-badge">{cat.icon}</span>
                  <div className="category-row-info">
                    <strong>{cat.name}</strong>
                    {cat.description && <span className="category-desc">{cat.description}</span>}
                    <div className="subcat-tags">
                      {(cat.subcategories || []).map((s, i) => (
                        <span key={i} className="subcat-tag">{s.name}</span>
                      ))}
                      {(!cat.subcategories || cat.subcategories.length === 0) && (
                        <span className="subcat-tag empty">No sub-options</span>
                      )}
                    </div>
                  </div>
                  <div className="category-row-actions">
                    <button
                      className={`btn-toggle${cat.isActive ? ' active' : ''}`}
                      onClick={() => handleToggleCategoryActive(cat)}
                      title={cat.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {cat.isActive ? '✓ Active' : '✗ Hidden'}
                    </button>
                    <button className="btn-icon" onClick={() => openEditCategory(cat)}>✏️ Edit</button>
                    <button className="btn-icon btn-danger" onClick={() => handleDeleteCategory(cat._id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSection === 'report' && (
        <div className="section-content">
          <h2>Harvesting Reports and Data Logging</h2>
          <p className="section-description">
            Harvesting map entries, data categories, and reporting are now managed in CIP.
            Use the bridge below to open your tenant workspace.
          </p>

          {!cipReady && (
            <div className="error-message">
              Set REACT_APP_CIP_APP_URL to enable CIP bridge access.
            </div>
          )}

          {cipReady && !authToken && (
            <div className="error-message">
              Log in to RS-Education first to continue to CIP.
            </div>
          )}

          {canBridgeToCip && (
            <div className="action-buttons">
              <a href={cipReportBridgeUrl} target="_blank" rel="noreferrer" className="btn-primary">
                Open CIP Harvesting Workspace
              </a>
            </div>
          )}
        </div>
      )}

      {activeSection === 'contact' && (
        <div className="section-content">
          <h2>Contact Information</h2>
          <p className="section-description">
            Manage contact information displayed to users. You can add text, phone numbers, emails, 
            addresses, and even embed Google Maps.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {contactLoading ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <div className="contact-editor">
              <RichTextEditor
                value={contactContent}
                onChange={setContactContent}
                placeholder="Enter contact information here. Use the toolbar to format text or click 'HTML' to paste embed codes (like Google Maps)..."
              />
              
              <div className="editor-actions">
                <button 
                  className="btn-primary" 
                  onClick={handleSaveContact}
                  disabled={contactLoading}
                >
                  {contactLoading ? 'Saving...' : 'Save Contact Information'}
                </button>
              </div>

              <div className="editor-help">
                <h3>💡 Tips:</h3>
                <ul>
                  <li>Use headings (H2, H3) to organize sections</li>
                  <li>Click the <strong>&lt;/&gt; HTML</strong> button to embed Google Maps or other HTML content</li>
                  <li>To get a Google Maps embed code:
                    <ol>
                      <li>Go to <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google Maps</a></li>
                      <li>Search for your location</li>
                      <li>Click "Share" → "Embed a map"</li>
                      <li>Copy the HTML code and paste it using the HTML button</li>
                    </ol>
                  </li>
                  <li>Click <strong>📝 Source</strong> to view/edit the raw HTML</li>
                </ul>
              </div>

              {contactContent && (
                <div className="preview-section">
                  <h3>Preview</h3>
                  <div 
                    className="contact-preview" 
                    dangerouslySetInnerHTML={{ __html: contactContent }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Education Resource Modal */}
      {showEducationModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content education-modal${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingEducation ? 'Edit Post' : 'Create New Post'}</h3>
              <button className="close-btn" onClick={() => setShowEducationModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={educationFormData.title}
                  onChange={(e) => setEducationFormData({ ...educationFormData, title: e.target.value })}
                  placeholder="Enter post title (e.g., 'My First Moose Hunt Story')"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={educationFormData.category}
                  onChange={(e) => setEducationFormData({ ...educationFormData, category: e.target.value })}
                  required
                >
                  <option value="Study Guides">📚 Study Guides</option>
                  <option value="Test Information">📝 Test Information</option>
                  <option value="Campfire Stories">🔥 Campfire Stories</option>
                  <option value="Community Stories">👥 Community Stories</option>
                  <option value="Traditional Knowledge">🌿 Traditional Knowledge</option>
                  <option value="Safety Guidelines">⚠️ Safety Guidelines</option>
                  <option value="Identification Guides">🔍 Identification Guides</option>
                  <option value="Seasonal Calendars">📅 Seasonal Calendars</option>
                  <option value="Processing & Preservation">🥫 Processing & Preservation</option>
                  <option value="Regulations & Permits">📋 Regulations & Permits</option>
                  <option value="Videos & Tutorials">🎥 Videos & Tutorials</option>
                  <option value="Downloadable Resources">📥 Downloadable Resources</option>
                  <option value="Workshop Materials">🎓 Workshop Materials</option>
                  <option value="Announcements">📢 Announcements</option>
                  <option value="Other">📍 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Short Description *</label>
                <textarea
                  value={educationFormData.description}
                  onChange={(e) => setEducationFormData({ ...educationFormData, description: e.target.value })}
                  placeholder="Brief summary (shown in post preview)"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={educationFormData.content}
                  onChange={(content) => setEducationFormData({ ...educationFormData, content })}
                  placeholder="Share your story, guide, or information here..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="url"
                    value={educationFormData.thumbnailUrl}
                    onChange={(e) => setEducationFormData({ ...educationFormData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL (YouTube, Vimeo)</label>
                  <input
                    type="url"
                    value={educationFormData.videoUrl}
                    onChange={(e) => setEducationFormData({ ...educationFormData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="url"
                  value={educationFormData.externalLink}
                  onChange={(e) => setEducationFormData({ ...educationFormData, externalLink: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={educationFormData.tags.join(', ')}
                  onChange={(e) => setEducationFormData({ 
                    ...educationFormData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="traditional, safety, beginner"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={educationFormData.order}
                    onChange={(e) => setEducationFormData({ ...educationFormData, order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={educationFormData.isPublished}
                    onChange={(e) => setEducationFormData({ ...educationFormData, isPublished: e.target.checked })}
                  />
                  <span>Published (visible to users)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={educationFormData.isFeatured}
                    onChange={(e) => setEducationFormData({ ...educationFormData, isFeatured: e.target.checked })}
                  />
                  <span>Featured (highlighted on page)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowEducationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveEducation}
                disabled={!educationFormData.title || !educationFormData.description || !educationFormData.content || loading}
              >
                {loading ? 'Saving...' : (editingEducation ? 'Update Post' : 'Create Post')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information & Permits Modal */}
      {showInformationModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content education-modal${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingInformation ? 'Edit Information' : 'Create Information'}</h3>
              <button className="close-btn" onClick={() => setShowInformationModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={informationFormData.title}
                  onChange={(e) => setInformationFormData({ ...informationFormData, title: e.target.value })}
                  placeholder="Enter title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={informationFormData.category}
                  onChange={(e) => setInformationFormData({ ...informationFormData, category: e.target.value })}
                  required
                >
                  <option value="Permits & Licenses">📋 Permits & Licenses</option>
                  <option value="Regulations">⚖️ Regulations</option>
                  <option value="Guidelines">📖 Guidelines</option>
                  <option value="Forms & Applications">📝 Forms & Applications</option>
                  <option value="Seasonal Restrictions">📅 Seasonal Restrictions</option>
                  <option value="Bag Limits">🎯 Bag Limits</option>
                  <option value="Protected Areas">🛡️ Protected Areas</option>
                  <option value="Safety Requirements">⚠️ Safety Requirements</option>
                  <option value="Reporting Requirements">📊 Reporting Requirements</option>
                  <option value="Contact Information">📞 Contact Information</option>
                  <option value="FAQs">❓ FAQs</option>
                  <option value="Updates & Changes">🔔 Updates & Changes</option>
                  <option value="Other">📄 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Short Description *</label>
                <textarea
                  value={informationFormData.description}
                  onChange={(e) => setInformationFormData({ ...informationFormData, description: e.target.value })}
                  placeholder="Brief summary"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={informationFormData.content}
                  onChange={(content) => setInformationFormData({ ...informationFormData, content })}
                  placeholder="Enter detailed information..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="url"
                    value={informationFormData.thumbnailUrl}
                    onChange={(e) => setInformationFormData({ ...informationFormData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL</label>
                  <input
                    type="url"
                    value={informationFormData.videoUrl}
                    onChange={(e) => setInformationFormData({ ...informationFormData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="url"
                  value={informationFormData.externalLink}
                  onChange={(e) => setInformationFormData({ ...informationFormData, externalLink: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Document Attachments (PDF, Word)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                />
                {uploadingFile && <p className="upload-status">Uploading...</p>}
                
                {informationFormData.attachments && informationFormData.attachments.length > 0 && (
                  <div className="attachments-list">
                    {informationFormData.attachments.map((attachment, index) => (
                      <div key={index} className="attachment-item">
                        <span className="attachment-icon">📄</span>
                        <span className="attachment-name">{attachment.filename}</span>
                        <span className="attachment-size">({(attachment.size / 1024).toFixed(2)} KB)</span>
                        <button
                          type="button"
                          className="remove-attachment-btn"
                          onClick={() => handleRemoveAttachment(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Effective Date</label>
                  <input
                    type="date"
                    value={informationFormData.effectiveDate}
                    onChange={(e) => setInformationFormData({ ...informationFormData, effectiveDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={informationFormData.expiryDate}
                    onChange={(e) => setInformationFormData({ ...informationFormData, expiryDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={informationFormData.tags.join(', ')}
                  onChange={(e) => setInformationFormData({ 
                    ...informationFormData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="permits, hunting, fishing"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={informationFormData.order}
                    onChange={(e) => setInformationFormData({ ...informationFormData, order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={informationFormData.isPublished}
                    onChange={(e) => setInformationFormData({ ...informationFormData, isPublished: e.target.checked })}
                  />
                  <span>Published (visible to users)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={informationFormData.isFeatured}
                    onChange={(e) => setInformationFormData({ ...informationFormData, isFeatured: e.target.checked })}
                  />
                  <span>Featured (highlighted on page)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowInformationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveInformation}
                disabled={!informationFormData.title || !informationFormData.description || !informationFormData.content || loading}
              >
                {loading ? 'Saving...' : (editingInformation ? 'Update Information' : 'Create Information')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
          <div className="modal-content" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button className="close-btn" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group" style={{ flex: '0 0 64px' }}>
                  <label>Icon</label>
                  <input
                    type="text"
                    value={categoryFormData.icon}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, icon: e.target.value })}
                    style={{ fontSize: 22, textAlign: 'center', padding: '6px 4px' }}
                    maxLength={4}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Name *</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    placeholder="e.g. Fishing, Hunting, Plants & Medicine"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  placeholder="Optional short description"
                />
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={categoryFormData.isActive}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, isActive: e.target.checked })}
                  />
                  <span>Active (visible to community members)</span>
                </label>
              </div>

              <div className="form-group">
                <label>Method / Context Options (sub-options)</label>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>These appear in the "Method / Context" dropdown when logging activity.</p>
                <div className="subcat-input-row">
                  <input
                    type="text"
                    value={newSubcatName}
                    onChange={(e) => setNewSubcatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcat(); } }}
                    placeholder="e.g. From Boat, Tree Stand, Berry Picking…"
                  />
                  <button type="button" className="btn-secondary" onClick={addSubcat}>Add</button>
                </div>
                <div className="subcat-tags" style={{ marginTop: 8 }}>
                  {categoryFormData.subcategories.map((s, i) => (
                    <span key={i} className="subcat-tag editable">
                      {s.name}
                      <button type="button" onClick={() => removeSubcat(i)} className="subcat-remove">&times;</button>
                    </span>
                  ))}
                  {categoryFormData.subcategories.length === 0 && (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>No options added yet.</span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSaveCategory}
                disabled={!categoryFormData.name.trim() || loading}
              >
                {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StaffHarvestingPage;
